<script setup lang="ts">
import { onErrorCaptured, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import ErrorFallback from '@/components/ErrorFallback.vue'
import NewVersionModal from '@/components/NewVersionModal.vue'
import { useSafeAreaInjection, useBackGestureReporter, useOfflineGuard } from '@/composables/useAppShell'
import { useVersionCheck } from '@/composables/useVersionCheck'

defineOptions({ name: 'AppRoot' })

const route = useRoute()
const { t } = useI18n()

// 壳层关注点抽到 composables，App.vue 保持薄组合层
useSafeAreaInjection()
useBackGestureReporter()
const { isOffline } = useOfflineGuard()
// 应用级版本检测：发现新版本时弹出全局更新弹窗
const { visible: versionVisible, versionData, close: closeVersionModal } = useVersionCheck()

// 错误边界（onErrorCaptured 仅捕获子树渲染/生命周期错误；async/事件错误由 app.config.errorHandler 兜底）
const error = shallowRef<unknown>(null)
const errorKey = shallowRef(0)
onErrorCaptured((err) => {
  error.value = err
  return false
})
watch(() => route.fullPath, () => {
  error.value = null
  errorKey.value++
})
function resetError() {
  error.value = null
}

// KeepAlive 缓存清单（显式指定；页面须 defineOptions({ name })；:max 兜底防无限增长）
// TabHome 壳：进入子页再返回时保留 tab 状态（visitedTabs 等）
const cachedNames = ['TabHomePage']
</script>

<template>
  <ErrorFallback v-if="error" :error="error" :key="errorKey" @reset="resetError" />
  <div v-else-if="isOffline" class="flex flex-col w-screen h-screen">
    <div class="pt-inset-top px-4 h-12 flex items-center text-ink">{{ t('offline') }}</div>
    <div class="flex flex-1 items-center justify-center text-muted text-sm">{{ t('networkError') }}</div>
    <div class="pb-inset-bottom" />
  </div>
  <RouterView v-else v-slot="{ Component }">
    <KeepAlive :include="cachedNames" :max="10">
      <component :is="Component" />
    </KeepAlive>
  </RouterView>
  <!-- 全局版本更新弹窗（Web 页面由 composable 内部跳过） -->
  <NewVersionModal :visible="versionVisible" :version-data="versionData" @close="closeVersionModal" />
</template>
